<script setup lang="ts">
import { computed } from 'vue'
import {
  ArrowRight,
  Building2,
  Bell,
  Calendar,
  Camera,
  Check,
  ChevronDown,
  CircleAlert,
  CircleCheckBig,
  Clock,
  Cookie,
  Globe,
  Heart,
  HousePlus,
  Info,
  Lightbulb,
  MapPin,
  MessageCircle,
  Moon,
  Palette,
  Search,
  Split,
  Sun,
  UserCog,
  Users,
  UserX,
  X,
  Ticket,
  Tickets,
  WalletCards,
} from 'lucide-vue-next'

const ICONS = {
  ArrowRight,
  Building2,
  Bell,
  Calendar,
  Camera,
  Check,
  ChevronDown,
  CircleAlert,
  CircleCheckBig,
  Clock,
  Cookie,
  Globe,
  Heart,
  HousePlus,
  Info,
  Lightbulb,
  MapPin,
  MessageCircle,
  Moon,
  Palette,
  Search,
  Split,
  Sun,
  UserCog,
  Users,
  UserX,
  X,
  Ticket,
  Tickets,
  WalletCards,
} as const

type IconName = keyof typeof ICONS

const ICON_ALIASES: Record<string, IconName> = {
  'arrow-right': 'ArrowRight',
  arrowright: 'ArrowRight',
  checkmark: 'Check',
  check: 'Check',
  'circle-check-big': 'CircleCheckBig',
  circlecheckbig: 'CircleCheckBig',
  clock: 'Clock',
  cookiebite: 'Cookie',
  maplocation: 'MapPin',
  'message-circle': 'MessageCircle',
  messagecircle: 'MessageCircle',
  ticketssimple: 'Tickets',
  tickets: 'Tickets',
  'triangle-alert': 'CircleAlert',
  trianglealert: 'CircleAlert',
  usergear: 'UserCog',
  usercog: 'UserCog',
  xmark: 'X',
  close: 'X',
}

const FALLBACK_ICON_NAME: IconName = 'CircleAlert'

type Props = {
  name: string
  fallbackName?: string
  size?: number
  strokeWidth?: number
  class?: string
}

const props = withDefaults(defineProps<Props>(), {
  fallbackName: FALLBACK_ICON_NAME,
  size: 24,
  strokeWidth: 2,
  class: '',
})

function resolveIconName(name?: string): IconName | null {
  if (!name) return null

  const normalizedName = name.trim()
  const exactName = normalizedName as IconName

  if (ICONS[exactName]) {
    return exactName
  }

  return ICON_ALIASES[normalizedName.toLowerCase()] ?? null
}

const component = computed(() => {
  const iconName = resolveIconName(props.name)
  const fallbackName = resolveIconName(props.fallbackName) ?? FALLBACK_ICON_NAME

  return ICONS[iconName ?? fallbackName]
})
</script>

<template>
  <component
    :is="component"
    :size="size"
    :stroke-width="strokeWidth"
    :class="props.class"
    aria-hidden="true"
  />
</template>
